// apps/web/src/features/crm/leads/card/LeadCardChecklist.tsx
//
// CLOSING checklist section (#2247 FR-2), re-skinned into the `SectionCard`
// rhythm (#2315). Guarded by data: it fetches only for a CLOSING-phase
// projection and renders nothing when the projection carries no seeded items
// (a coexistence-projected row has none), so a plain LEAD-phase card degrades
// cleanly and no dead/empty control appears (BR-6). The server-computed
// `progress` is rendered verbatim (never recomputed).
//
// #2317 D-2: an editor (admin/assigned, `canEditCard`) can TOGGLE an item's
// TODO/DONE status via `useToggleLeadChecklistItem` (`PATCH
// /leads/:id/checklist/:itemId`). Item 7 (`confirm_payment`) renders DISABLED
// and NEVER fires the toggle — payment is confirmed only by the paid-stage
// transition. A read-only viewer keeps the static, non-interactive rows.
import { useTranslation } from 'react-i18next';
import {
  ChecklistItemStatus,
  CLOSING_CONVERSION_TRIGGER_KEY,
  EngagementPhase,
  type ChecklistItemView,
  type PatchChecklistItemInput,
} from '@magimp/shared';
import { useEngagementChecklist, useToggleLeadChecklistItem } from '@magimp/shared-hooks';
import { Icon, SectionCard } from '../../../../ui/index.js';
import { useToastStore } from '../../../../stores/toast.js';
import { errorMessage } from '../../../../api/errors.js';

export interface LeadCardChecklistProps {
  leadId: string;
  engagementId: string;
  phase: EngagementPhase;
  /** #2317: admin-or-assigned gate that unlocks item toggling. */
  canEditCard: boolean;
}

export function LeadCardChecklist({
  leadId,
  engagementId,
  phase,
  canEditCard,
}: LeadCardChecklistProps) {
  const { t } = useTranslation(['leads']);
  const enabled = phase === EngagementPhase.CLOSING;
  const q = useEngagementChecklist(engagementId, { enabled });

  if (!enabled || !q.isSuccess || q.data.items.length === 0) return null;
  const { items, progress } = q.data;
  const pct = progress.totalActive > 0 ? (progress.doneActive / progress.totalActive) * 100 : 0;

  const title = (
    <>
      {t('leads:checklist.title')}
      <span
        style={{
          marginLeft: 8,
          fontWeight: 400,
          textTransform: 'none',
          letterSpacing: 'normal',
          fontSize: 'var(--fz-12)',
          color: 'var(--fg-2)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {t('leads:checklist.progress', { done: progress.doneActive, total: progress.totalActive })}
      </span>
    </>
  );

  const progressBar = (
    <div
      aria-hidden="true"
      style={{
        width: 160,
        height: 4,
        borderRadius: 2,
        background: 'var(--bg-3)',
        overflow: 'hidden',
      }}
    >
      <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent-1)' }} />
    </div>
  );

  return (
    <div data-testid="lead-card-checklist">
      <SectionCard title={title} action={progressBar}>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.map((item, i) => (
            <ChecklistRow
              key={item.id}
              leadId={leadId}
              engagementId={engagementId}
              item={item}
              first={i === 0}
              canEditCard={canEditCard}
            />
          ))}
        </ul>
      </SectionCard>
    </div>
  );
}

function ChecklistRow({
  leadId,
  engagementId,
  item,
  first,
  canEditCard,
}: {
  leadId: string;
  engagementId: string;
  item: ChecklistItemView;
  first: boolean;
  canEditCard: boolean;
}) {
  const { t } = useTranslation(['leads']);
  const show = useToastStore((s) => s.show);
  const toggle = useToggleLeadChecklistItem(leadId, engagementId, item.id);

  // WHY: literal `t('leads:checklist.items.X')` per template key — i18next's
  // typed-key contract rejects a runtime `t(dynamicKey)`. An unknown key (a
  // template the UI predates) falls back to the raw key.
  const labels: Record<string, string> = {
    contract_legal_entities_check: t('leads:checklist.items.contract_legal_entities_check'),
    spec: t('leads:checklist.items.spec'),
    contract: t('leads:checklist.items.contract'),
    issue_invoice: t('leads:checklist.items.issue_invoice'),
    send_invoice: t('leads:checklist.items.send_invoice'),
    receive_payment_slip: t('leads:checklist.items.receive_payment_slip'),
    confirm_payment: t('leads:checklist.items.confirm_payment'),
  };
  const label = labels[item.templateKey] ?? item.templateKey;
  const done = item.status === ChecklistItemStatus.DONE;
  const skipped = item.status === ChecklistItemStatus.SKIPPED || !item.active;
  // Item 7 is the conversion trigger — confirmed only via the paid-stage
  // transition, never toggled from the card.
  const isConfirmPayment = item.templateKey === CLOSING_CONVERSION_TRIGGER_KEY;
  const interactive = canEditCard && !isConfirmPayment;

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '7px 0',
    borderTop: first ? 'none' : '1px solid var(--border-2)',
    fontSize: 13,
    color: skipped ? 'var(--fg-3)' : 'var(--fg-1)',
    textDecoration: skipped ? 'line-through' : 'none',
    textDecorationColor: 'var(--fg-4)',
  } as const;

  const icon = (
    <Icon
      name={done ? 'check-circle-2' : 'circle-dot'}
      size={17}
      style={{ color: done ? 'var(--status-success-fg)' : 'var(--fg-3)' }}
    />
  );
  const optionalBadge = item.isOptional ? (
    <span
      style={{
        fontSize: 10,
        color: 'var(--fg-4)',
        border: '1px solid var(--border-2)',
        borderRadius: 'var(--r-1)',
        padding: '1px 5px',
      }}
    >
      {t('leads:checklist.optional')}
    </span>
  ) : null;

  const onToggle = () => {
    // D-2 (mirrors native `patchItem`): an OPTIONAL item toggles `active`
    // (include ↔ N/A); a REQUIRED item flips TODO/DONE. Exactly one field.
    const payload: PatchChecklistItemInput = item.isOptional
      ? { active: !item.active }
      : { status: done ? ChecklistItemStatus.TODO : ChecklistItemStatus.DONE };
    toggle.mutate(payload, {
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  const buttonReset = {
    background: 'transparent',
    border: 0,
    padding: 0,
    margin: 0,
    font: 'inherit',
    color: 'inherit',
    textDecoration: 'inherit',
    width: '100%',
    textAlign: 'left',
  } as const;

  return (
    <li data-testid="lead-card-checklist-item" data-status={item.status} style={rowStyle}>
      {interactive ? (
        <button
          type="button"
          data-testid="lead-card-checklist-toggle"
          onClick={onToggle}
          disabled={toggle.isPending}
          aria-label={label}
          style={{
            ...buttonReset,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: toggle.isPending ? 'default' : 'pointer',
          }}
        >
          {icon}
          <span style={{ flex: 1 }}>{label}</span>
          {optionalBadge}
        </button>
      ) : (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}
          data-testid={isConfirmPayment && canEditCard ? 'lead-card-checklist-blocked' : undefined}
        >
          {icon}
          <span style={{ flex: 1 }}>{label}</span>
          {optionalBadge}
          {isConfirmPayment && canEditCard && (
            <span
              data-testid="lead-card-confirm-payment-hint"
              style={{ fontSize: 11, color: 'var(--fg-3)', whiteSpace: 'nowrap' }}
            >
              {t('leads:checklist.confirmPaymentHint')}
            </span>
          )}
        </div>
      )}
    </li>
  );
}
